package com.writerslab.office

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun FinancePage() {
    var users by remember { mutableStateOf<List<OfficeUser>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        if (PREVIEW) { users = MockApi.users(); return@LaunchedEffect }
        try { users = ApiClient.getUsers() }
        catch (e: Throwable) { error = e.message ?: "Ошибка загрузки" }
    }

    Div({ style { padding(24.px) } }) {
        H2({ style { color(Color(C.OnSurface)); marginBottom(20.px); fontSize(18.px) } }) {
            Text("Финансы и активность")
        }

        when {
            error != null -> Span({ style { color(Color(C.Error)) } }) { Text(error!!) }
            users == null -> Span({ style { color(Color(C.Muted)) } }) { Text("Загрузка…") }
            else -> {
                val list = users!!
                val totalBalance = list.sumOf { it.balance }
                val totalPaid = list.sumOf { it.totalPaidCredits }
                val totalCode = list.sumOf { it.totalCodeCredits }
                val totalSpent = list.sumOf { it.pipelineCreditsCharged }

                // Summary cards
                Div({
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(4, 1fr)")
                        property("gap", "12px")
                        marginBottom(24.px)
                    }
                }) {
                    SummaryCard("Баланс в обращении", totalBalance.toString(), C.Primary)
                    SummaryCard("Куплено кредитов", totalPaid.toString(), C.Success)
                    SummaryCard("Получено по кодам", totalCode.toString(), C.Warning)
                    SummaryCard("Потрачено (пайплайн)", totalSpent.toString(), C.Muted)
                }

                // === Финансовая таблица ===
                SectionTitle("Балансы")
                Div({ style { property("overflow-x", "auto"); marginBottom(32.px) } }) {
                    Table({ style { tableStyle() } }) {
                        Thead {
                            Tr {
                                Th { Text("Email") }
                                Th { Text("Баланс") }
                                Th { Text("Куплено") }
                                Th { Text("По коду") }
                                Th { Text("Потрачено") }
                                Th { Text("Книг") }
                                Th { Text("Регистрация") }
                            }
                        }
                        Tbody {
                            list.forEach { u ->
                                val spent = u.pipelineCreditsCharged
                                Tr {
                                    Td {
                                        Div { Text(u.email) }
                                        Span({
                                            style { mono(); fontSize(10.px); color(Color(C.Muted)) }
                                            attr("title", u.userId)
                                        }) { Text(u.userId.take(14) + if (u.userId.length > 14) "…" else "") }
                                    }
                                    Td {
                                        Span({
                                            style {
                                                color(Color(if (u.balance > 0) C.Success else C.Muted))
                                                property("font-weight", "600")
                                            }
                                        }) { Text(u.balance.toString()) }
                                    }
                                    Td { Text(if (u.totalPaidCredits > 0) u.totalPaidCredits.toString() else "—") }
                                    Td { Text(if (u.totalCodeCredits > 0) u.totalCodeCredits.toString() else "—") }
                                    Td { Text(if (spent > 0) spent.toString() else "—") }
                                    Td { Text(u.bookCount.toString()) }
                                    Td {
                                        Span({ style { color(Color(C.Muted)); fontSize(12.px) } }) {
                                            Text(u.createdAt.take(10))
                                        }
                                    }
                                }
                            }
                            TotalRow(
                                listOf(
                                    "Итого (${list.size})",
                                    totalBalance.toString(),
                                    totalPaid.toString(),
                                    totalCode.toString(),
                                    totalSpent.toString(),
                                    list.sumOf { it.bookCount }.toString(),
                                    "",
                                )
                            )
                        }
                    }
                }

                // === Таблица активности ===
                SectionTitle("Активность по типам анализа")
                Div({ style { property("overflow-x", "auto") } }) {
                    Table({ style { tableStyle() } }) {
                        Thead {
                            Tr {
                                Th { Text("Email") }
                                Th { Text("Анализ (платно)") }
                                Th { Text("Анализ (кредитов)") }
                                Th { Text("Аннот. платно / free") }
                                Th { Text("Обложки batch платно / free") }
                                Th { Text("Regen платно") }
                                Th { Text("Тизеры платно / free") }
                            }
                        }
                        Tbody {
                            list.forEach { u ->
                                Tr {
                                    Td { Text(u.email) }
                                    Num(u.pipelineJobsDone)
                                    Num(u.pipelineCreditsCharged)
                                    Td {
                                        Span({ style { color(Color(C.Primary)) } }) { Text(u.paidAnnotationsUsed.toString()) }
                                        Span({ style { color(Color(C.Muted)) } }) { Text(" / ${u.freeAnnotationsUsed}") }
                                    }
                                    Td {
                                        Span({ style { color(Color(C.Primary)) } }) { Text(u.paidCoverBatchesUsed.toString()) }
                                        Span({ style { color(Color(C.Muted)) } }) { Text(" / ${u.freeCoverBatchesUsed}") }
                                    }
                                    Num(u.paidCoverRegenUsed)
                                    Td {
                                        Span({ style { color(Color(C.Primary)) } }) { Text(u.paidTeaserPreviewsUsed.toString()) }
                                        Span({ style { color(Color(C.Muted)) } }) { Text(" / ${u.freeTeaserPreviewsUsed}") }
                                    }
                                }
                            }
                            TotalRow(
                                listOf(
                                    "Итого",
                                    list.sumOf { it.pipelineJobsDone }.toString(),
                                    list.sumOf { it.pipelineCreditsCharged }.toString(),
                                    "${list.sumOf { it.paidAnnotationsUsed }}/${list.sumOf { it.freeAnnotationsUsed }}",
                                    "${list.sumOf { it.paidCoverBatchesUsed }}/${list.sumOf { it.freeCoverBatchesUsed }}",
                                    list.sumOf { it.paidCoverRegenUsed }.toString(),
                                    "${list.sumOf { it.paidTeaserPreviewsUsed }}/${list.sumOf { it.freeTeaserPreviewsUsed }}",
                                )
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Div({ style { color(Color(C.Muted)); fontSize(11.px); property("font-weight", "600"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); marginBottom(8.px) } }) {
        Text(text)
    }
}

@Composable
private fun Num(value: Int) {
    Td {
        if (value > 0) Text(value.toString())
        else Span({ style { color(Color(C.Muted)) } }) { Text("—") }
    }
}

@Composable
private fun TotalRow(cells: List<String>) {
    Tr({
        style {
            backgroundColor(Color(C.SurfaceHigh))
            property("font-weight", "600")
        }
    }) {
        cells.forEach { Td { Text(it) } }
    }
}

@Composable
private fun SummaryCard(label: String, value: String, valueColor: String = C.OnSurface) {
    Div({
        style {
            backgroundColor(Color(C.Surface))
            property("border", "1px solid ${C.Border}")
            property("border-radius", "8px")
            padding(16.px)
        }
    }) {
        Div({ style { color(Color(C.Muted)); fontSize(12.px); marginBottom(4.px) } }) { Text(label) }
        Div({ style { color(Color(valueColor)); fontSize(24.px); property("font-weight", "700") } }) { Text(value) }
    }
}
